import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
    Grid, Paper, TextField, MenuItem, Button,
    Box, IconButton, Divider
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Title from '../../component/Title'
import Snackbars from '../../component/Snackbars'
import API from '../../component/Axios'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppAccessRuleDraft,
    clearAppAccessRuleDraft,
    createAppAccessRuleDraftPayload,
    NEW_APP_ACCESS_RULE_DRAFT_KEY,
    readAppAccessRuleDraft,
    saveAppAccessRuleDraft,
} from './appAccessRuleDraftUtils'

const ACTION_TYPES = [
    { value: 'FORCE_EXIT', label: '강제 종료' },
    { value: 'NOTICE', label: '공지' }
]

const CONDITION_FIELDS = [
    'COUNTRY',
    'DEVICE_TYPE',
    'DEVICE_MODEL',
    'APP_VERSION',
    'OS_VERSION'
]

const OPERATORS_BY_FIELD = {
    COUNTRY: ['=', '!='],
    DEVICE_TYPE: ['=', '!='],
    DEVICE_MODEL: ['=', '!='],

    APP_VERSION: ['=', '!=', '<', '<=', '>', '>='],
    OS_VERSION: ['=', '!=', '<', '<=', '>', '>=']
}

const TARGET_TYPES = [
    'ALL',
    'USER_ID',
    // 'PHONE',
    // 'EMAIL',
    'DEVICE_FINGERPRINT'
]

export default function NewAppAccessRule() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || { actionType: 'ALL', isActive: 'ALL' }
    const listSearch = location.search || ''
    const [saving, setSaving] = useState(false)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const initialDraftSnapshotRef = useRef('')

    const [form, setForm] = useState({
        title: '',
        content: '',
        actionType: 'NOTICE',
        priority: 0,
        isActive: true
    })

    const [conditions, setConditions] = useState([])
    const [targets, setTargets] = useState([])

    useEffect(() => {
        const baseForm = {
            title: '',
            content: '',
            actionType: 'NOTICE',
            priority: 0,
            isActive: true
        }
        const baseConditions = []
        const baseTargets = []
        const storedDraft = readAppAccessRuleDraft(NEW_APP_ACCESS_RULE_DRAFT_KEY)
        const restoredDraft = applyAppAccessRuleDraft({
            baseForm,
            baseConditions,
            baseTargets,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppAccessRuleDraftPayload({
            form: baseForm,
            conditions: baseConditions,
            targets: baseTargets,
        }))

        setForm((prev) => ({
            ...prev,
            ...restoredDraft.form,
        }))
        setConditions(restoredDraft.conditions)
        setTargets(restoredDraft.targets)
        setDraftReady(true)
    }, [])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 앱 접근 정책 등록은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppAccessRuleDraftPayload({
            form,
            conditions,
            targets,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppAccessRuleDraft(NEW_APP_ACCESS_RULE_DRAFT_KEY)
            return
        }

        saveAppAccessRuleDraft(NEW_APP_ACCESS_RULE_DRAFT_KEY, nextDraft)
    }, [conditions, draftReady, form, targets])

    const normalizeTargets = (targetRows) => {
        return (targetRows || []).map(target => {
            const targetType = target?.targetType || 'ALL'

            if (targetType === 'ALL') {
                return { targetType, value: '' }
            }

            return {
                targetType,
                value: target?.value ? String(target.value).trim() : ''
            }
        })
    }

    const normalizeConditions = (conditionRows) => {
        return (conditionRows || []).map(condition => ({
            ...condition,
            conditionType: condition?.conditionType || '',
            operator: condition?.operator || '',
            value: condition?.value ? String(condition.value).trim() : ''
        }))
    }

    const createData = async (payload) => {
        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appAccessRule/create', payload)

            if (data.code === 200) {
                // 등록 후에도 이전 앱 접근 정책 목록 조건으로 그대로 복귀시킨다.
                clearAppAccessRuleDraft(NEW_APP_ACCESS_RULE_DRAFT_KEY)
                history.push(createListLinkTarget({
                    pathname: `/appAccessRules`,
                    search: listSearch,
                    openSnackbar: true,
                    severitySnackbar: 'success',
                    messageSnackbar: data.message,
                    curPage: listCurPage,
                    searchText: listSearchText,
                    searchSelect: listSearchSelect
                }))
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('앱 접근 정책을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    /* ======================
       핸들러
    ====================== */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (form.title || '').trim()
        const nextContent = (form.content || '').trim()
        const normalizedConditions = normalizeConditions(conditions)
        const normalizedTargets = normalizeTargets(targets)
        const invalidConditionIndex = normalizedConditions.findIndex((condition) => !condition.conditionType || !condition.operator || !condition.value)
        const invalidTargetIndex = normalizedTargets.findIndex((target) => target.targetType !== 'ALL' && !target.value)

        setForm((prev) => ({
            ...prev,
            title: nextTitle,
            content: nextContent
        }))

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('제목을 입력하세요.')
            return
        }

        if (!nextContent) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('내용을 입력하세요.')
            return
        }

        if (invalidConditionIndex !== -1) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(`조건 ${invalidConditionIndex + 1}의 값을 모두 입력하세요.`)
            return
        }

        if (invalidTargetIndex !== -1) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(`타겟 ${invalidTargetIndex + 1}의 값을 입력하세요.`)
            return
        }

        const payload = {
            ...form,
            title: nextTitle,
            content: nextContent,
            conditions: normalizedConditions,
            targets: normalizedTargets
        }

        createData(payload)
    }

    const handleClose = () => {
        setOpenSnackbar(false)
    }
    
    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 3 }} component="form" onSubmit={handleSubmit}>
                <Title>앱 접근 정책 등록</Title>

                {/* 기본 정보 */}
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="제목"
                        fullWidth
                        value={form.title}
                        disabled={saving}
                        onChange={e => setForm({ ...form, title: e.target.value })}
                    />
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="내용"
                            value={form.content}
                            multiline
                            minRows={4}
                            maxRows={10}
                            disabled={saving}
                            onChange={e => setForm({ ...form, content: e.target.value })}
                            sx={{ mt: 2 }}
                            placeholder="사용자에게 노출될 메시지를 입력하세요"
                        />
                    </Grid>

                    <TextField
                        select
                        label="Action Type"
                        value={form.actionType}
                        disabled={saving}
                        onChange={e => setForm({ ...form, actionType: e.target.value })}
                        sx={{ mt: 2 }}
                        fullWidth
                    >
                        {ACTION_TYPES.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        type="number"
                        label="Priority"
                        value={form.priority}
                        disabled={saving}
                        onChange={e => setForm({ ...form, priority: Number(e.target.value) })}
                        sx={{ mt: 2 }}
                        fullWidth
                    />
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* 조건 */}
                <Title>조건</Title>
                {conditions.map((cond, idx) => {
                    const operators = OPERATORS_BY_FIELD[cond.conditionType] || ['=']

                    return (
                        <Box key={idx} display="flex" gap={1} sx={{ mt: 1 }}>
                            {/* conditionType */}
                            <TextField
                                select
                                value={cond.conditionType}
                                sx={{ width: 230 }}
                                disabled={saving}
                                onChange={e => {
                                    const next = [...conditions]
                                    const newType = e.target.value
                                    const validOps = OPERATORS_BY_FIELD[newType] || ['=']

                                    next[idx] = {
                                        ...next[idx],
                                        conditionType: newType,
                                        operator: validOps[0] // ⭐ 자동 보정
                                    }
                                    setConditions(next)
                                }}
                            >
                                {CONDITION_FIELDS.map(f => (
                                    <MenuItem key={f} value={f}>{f}</MenuItem>
                                ))}
                            </TextField>

                            {/* operator */}
                            <TextField
                                select
                                value={cond.operator}
                                disabled={saving}
                                onChange={e => {
                                    const next = [...conditions]
                                    next[idx].operator = e.target.value
                                    setConditions(next)
                                }}
                            >
                                {operators.map(op => (
                                    <MenuItem key={op} value={op}>{op}</MenuItem>
                                ))}
                            </TextField>

                            {/* value */}
                            <TextField
                                value={cond.value}
                                disabled={saving}
                                onChange={e => {
                                    const next = [...conditions]
                                    next[idx].value = e.target.value
                                    setConditions(next)
                                }}
                            />

                            <IconButton
                                disabled={saving}
                                onClick={() =>
                                    setConditions(conditions.filter((_, i) => i !== idx))
                                }
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )
                })}

                <Button
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                    disabled={saving}
                    onClick={() =>
                        setConditions([...conditions, { conditionType: 'DEVICE_TYPE', operator: '=', value: '' }])
                    }
                >
                    조건 추가
                </Button>

                <Divider sx={{ my: 3 }} />

                {/* 타겟 */}
                <Title>타겟</Title>
                {targets.map((t, idx) => (
                    <Box key={idx} display="flex" gap={1} sx={{ mt: 1 }}>
                        <TextField
                            select
                            value={t.targetType}
                            sx={{ width: 230 }}
                            disabled={saving}
                            onChange={e => {
                                const next = [...targets]
                                next[idx].targetType = e.target.value
                                if (e.target.value === 'ALL') {
                                    next[idx].value = ''
                                }
                                setTargets(next)
                            }}
                        >
                            {TARGET_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            value={t.value || ''}
                            disabled={saving}
                            onChange={e => {
                                const next = [...targets]
                                next[idx].value = e.target.value
                                setTargets(next)
                            }}
                            placeholder="값 (ALL은 비워두기)"
                        />

                        <IconButton disabled={saving} onClick={() =>
                            setTargets(targets.filter((_, i) => i !== idx))
                        }>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                ))}

                <Button
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                    disabled={saving}
                    onClick={() =>
                        setTargets([...targets, { targetType: 'ALL', value: '' }])
                    }
                >
                    타겟 추가
                </Button>

                <Divider sx={{ my: 3 }} />

                <BottomButtonGroup
                    path="/appAccessRules"
                    search={listSearch}
                    visible={true}
                    curPage={listCurPage}
                    searchText={listSearchText}
                    searchSelect={listSearchSelect}
                    onBackClick={() => clearAppAccessRuleDraft(NEW_APP_ACCESS_RULE_DRAFT_KEY)}
                    submitDisabled={saving}
                    submitLabel={saving ? '저장 중...' : '저장'}
                />

                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
